import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import App from './App.vue'
import getRouter from './router'
import { initStoreWatchers } from './store'
import { i18n } from '@/i18n'

import VueScrollTo from 'vue-scrollto'
import VueCookies from 'vue3-cookies'
import InlineSvg from 'vue-inline-svg'
import { setupSentryConsentGate } from '@/modules/sentry/init'

import PriceFormat from '@/plugins/PriceFormat'

import 'animejs' // side-effect import to include in the bundle
import '@/modules/Landing/LandingView.vue'
import analytics, { initContextWatchers } from '@/modules/analytics'
import { initAbPlatform } from '@/modules/abPlatform'
import { adoptCrmSessionFromUrl } from '@/helpers/crmSession'

// Best-effort delivery of pending events on tab close / navigation.
window.addEventListener('pagehide', () => {
	void analytics.flush()
})

// Handle chunk load failures (network issues, ad blockers).
// Non-critical chunks (consent, analytics, marketing) are silently swallowed.
// Critical chunks (route components, billing) trigger a single page reload.
window.addEventListener('vite:preloadError', (event) => {
	const message = event.payload.message

	const nonCriticalPatterns = ['event-schema', 'vendor-messaging', 'vendor-sentry', 'analytics-', 'vendor-braze']

	if (nonCriticalPatterns.some((p) => message.includes(p))) {
		event.preventDefault()
		return
	}

	// Critical chunk — reload once, then give up.
	// If chunk_retry is already present, we fall through without preventDefault(),
	// so Vite re-throws the error as an unhandled rejection (intentional "give up").
	const url = new URL(window.location.href)
	if (!url.searchParams.has('chunk_retry')) {
		event.preventDefault()
		url.searchParams.set('chunk_retry', '1')
		window.location.href = url.toString()
	}
})

const app = createApp(App)
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

app.use(pinia)

// CRM email links carry the user's original session_id (?from=email&session_id=…).
// Adopt it BEFORE analytics/Sentry seed context so every event + backend call
// (onboarding_session_id, Sentry user.id, trackUserAttributes, billing customer_id)
// rides the CRM-known id. Must run after app.use(pinia) and before initContextWatchers().
adoptCrmSessionFromUrl()

// Pinia is now active — wire analytics context watchers to store state.
// Has to happen after app.use(pinia); useMainStore() needs an active Pinia.
initContextWatchers()

// Kick off the AB-platform context fetch (DEV-35179). Ordering matters: after
// adoptCrmSessionFromUrl() — the experiment unit is the (possibly CRM-adopted)
// sessionId. First paint waits for the context via the status-store render gate
// ('abPlatform'), which the module opens on ready, failure or a hard deadline —
// a dead AB backend can delay the splash briefly, never hang it.
initAbPlatform()

// Arm Sentry: it initialises only once the user accepts all cookie categories.
// Subscribed here (before mount) so the first consent response is caught.
setupSentryConsentGate(app)

const router = getRouter()
app.use(router)
app.use(i18n)

app.use(PriceFormat)

app.use(VueScrollTo)
app.use(VueCookies)
app.component('InlineSvg', InlineSvg)

// Vue component errors are captured by @sentry/vue once Sentry.init({ app }) runs
// (after consent — see setupSentryConsentGate). initSentry also sets
// app.config.errorHandler: without a user-defined handler the SDK re-throws render
// errors and their `vue` context (componentName/trace) is lost to console capture +
// dedupe — see the comment in src/modules/sentry/init.ts.

// vue-i18n v9 legacy mode sets $t/$n/etc in beforeCreate() via app.mixin().
// Vue 3 blocks writes to $-prefixed properties but the code still works correctly —
// $n falls back to app.config.globalProperties.$n set during i18n install.
// This warnHandler suppresses the resulting noise in dev/staging.
app.config.warnHandler = (msg, _instance, trace) => {
	if (msg.startsWith('Attempting to mutate public property "$')) return
	console.warn(`[Vue warn]: ${msg}\n${trace}`)
}

initStoreWatchers()

app.mount('#app')
